import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Hook para carregar o SDK da Tuna Payments
 *
 * @param {Object} options
 * @param {boolean} options.enabled - Se deve carregar o SDK
 * @param {string} options.sessionId - ID da sessão do merchant (também usado para antifraude)
 * @param {boolean} options.sandbox - Se deve usar ambiente sandbox
 * @param {string} options.customerId - ID do cliente para antifraude (opcional - pode ser pedidoId, CPF, etc.)
 * @returns {Object} { tunaInstance, isLoading, error, isReady, antifraudSessionId }
 */
const useLoadTunaSDK = ({ enabled = false, sessionId = null, sandbox = false, customerId = null } = {}) => {
  const { t } = useTranslation('common');
  const [tunaInstance, setTunaInstance] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [antifraudSessionId, setAntifraudSessionId] = useState(null); // SessionID para antifraude (mesmo do Tuna)
  const loadAttemptRef = useRef(0);
  const scriptLoadedRef = useRef(false);

  // URL do SDK Tuna Essentials (versão mais estável)
  // Alterado de tuna-v2.js para tuna-essentials.js para evitar erro 404 no ListAntifrauds
  // Documentação: https://dev.tuna.uy/plugins/javascript/
  const TUNA_SDK_URL = 'https://js.tuna.uy/tuna-essentials.js';

  // Carregar script do SDK
  const loadScript = useCallback(() => {
    return new Promise((resolve, reject) => {
      // Verificar se já existe script carregado
      const existingScript = document.querySelector(`script[src="${TUNA_SDK_URL}"]`);
      if (existingScript) {
        console.log('[TUNA-SDK] Script já existe no DOM');
        if (window.Tuna) {
          resolve(window.Tuna);
          return;
        }
        // Script existe mas Tuna não está disponível, remover e recarregar
        existingScript.remove();
      }

      console.log('[TUNA-SDK] Carregando script:', TUNA_SDK_URL);

      const script = document.createElement('script');
      script.src = TUNA_SDK_URL;
      script.async = true;
      script.id = 'tuna-sdk-script';

      script.onload = () => {
        console.log('[TUNA-SDK] Script carregado com sucesso');
        scriptLoadedRef.current = true;

        // Aguardar Tuna estar disponível
        const checkTuna = (attempts = 0) => {
          if (window.Tuna) {
            resolve(window.Tuna);
          } else if (attempts < 50) {
            setTimeout(() => checkTuna(attempts + 1), 100);
          } else {
            reject(new Error('Tuna SDK não inicializou após carregamento'));
          }
        };
        checkTuna();
      };

      script.onerror = (e) => {
        console.error('[TUNA-SDK] Erro ao carregar script:', e);
        reject(new Error('Falha ao carregar Tuna SDK'));
      };

      document.head.appendChild(script);
    });
  }, [TUNA_SDK_URL]);

  // Inicializar SDK
  const initializeSDK = useCallback(async () => {
    if (!sessionId) {
      console.warn('[TUNA-SDK] SessionId não fornecido');
      return null;
    }

    try {
      const TunaSDK = await loadScript();

      // Determinar ambiente: 'sandbox' ou 'production' (ou undefined para produção)
      const env = sandbox ? 'sandbox' : 'production';

      console.log('[TUNA-SDK] Inicializando com sessionId:', sessionId?.substring(0, 20) + '...', 'env:', env);

      // ✅ Configuração do antifraude para CyberSource
      // IMPORTANTE: NÃO incluir "cybersource" com Key ID - isso corrompe o sessionId!
      // O SDK usa automaticamente o CyberSource configurado na conta Tuna
      // Apenas passar o sessionId para correlação do Device Fingerprint
      const antifraudConfig = [
        { key: "sessionid", value: sessionId },
        { key: "cybersource", value: "Tunaltda" }, // ✅ Merchant ID correto!
      ];

      console.log('[TUNA-SDK] 🛡️ AntifraudConfig:', {
        sessionid: sessionId?.substring(0, 20) + '...',
        customerid: customerId || 'usando sessionId',
        note: 'CyberSource usa config da conta Tuna automaticamente'
      });

      // Tuna(sessionID, env?, antifraudConfig?)
      const instance = TunaSDK(sessionId, env, antifraudConfig);

      console.log('[TUNA-SDK] ✅ Instância criada COM antifraud CyberSource');
      console.log('[TUNA-SDK] 🔍 Verificando métodos disponíveis:', Object.keys(instance || {}));

      // 🔥 CRÍTICO: Verificar se Device Fingerprint foi inicializado
      // O CyberSource precisa de tempo para coletar dados do navegador
      if (instance && typeof instance.startDeviceFingerprint === 'function') {
        console.log('[TUNA-SDK] 🛡️ Iniciando Device Fingerprint...');
        try {
          await instance.startDeviceFingerprint();
          console.log('[TUNA-SDK] ✅ Device Fingerprint iniciado com sucesso');
        } catch (dfError) {
          console.warn('[TUNA-SDK] ⚠️ Erro ao iniciar Device Fingerprint:', dfError);
        }
      } else {
        console.log('[TUNA-SDK] ℹ️ startDeviceFingerprint não disponível nesta versão do SDK');
      }

      return { instance, afSessionId: sessionId }; // Usa o mesmo sessionId
    } catch (err) {
      console.error('[TUNA-SDK] Erro ao inicializar:', err);
      throw err;
    }
  }, [sessionId, sandbox, customerId, loadScript]);

  // Efeito principal para carregar SDK
  useEffect(() => {
    if (!enabled) {
      setTunaInstance(null);
      setIsReady(false);
      setError(null);
      return;
    }

    if (!sessionId) {
      console.log('[TUNA-SDK] Aguardando sessionId...');
      return;
    }

    const load = async () => {
      loadAttemptRef.current += 1;
      const currentAttempt = loadAttemptRef.current;

      try {
        setIsLoading(true);
        setError(null);

        const result = await initializeSDK();

        // Verificar se ainda é a tentativa atual
        if (currentAttempt !== loadAttemptRef.current) {
          console.log('[TUNA-SDK] Tentativa obsoleta, ignorando');
          return;
        }

        if (result && result.instance) {
          setTunaInstance(result.instance);
          setAntifraudSessionId(result.afSessionId); // ✅ Salvar sessionId do antifraude
          setIsReady(true);
          console.log('[TUNA-SDK] ✅ SDK pronto para uso com antifraude');
          console.log('[TUNA-SDK] 🛡️ Antifraude sessionId salvo:', result.afSessionId);
        }
      } catch (err) {
        console.error('[TUNA-SDK] Falha ao carregar:', err);
        setError(err.message || t('tunaLoadError'));
        setIsReady(false);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [enabled, sessionId, initializeSDK]);

  // Função para recarregar SDK
  const reload = useCallback(() => {
    setTunaInstance(null);
    setIsReady(false);
    setError(null);

    // Remover script antigo
    const existingScript = document.getElementById('tuna-sdk-script');
    if (existingScript) {
      existingScript.remove();
    }
    scriptLoadedRef.current = false;

    // Forçar nova tentativa
    loadAttemptRef.current = 0;
  }, []);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      setTunaInstance(null);
      setIsReady(false);
    };
  }, []);

  return {
    tunaInstance,
    isLoading,
    error,
    isReady,
    reload,
    antifraudSessionId // ✅ NOVO: SessionID para enviar no frontData do pagamento
  };
};

export default useLoadTunaSDK;
